<template>
    <SelectButton
        :name="name"
        v-model="selected"
        :options="options"
        optionLabel="label"
        :class="classes"
        :disabled="disabled"
        :allowEmpty="allowEmpty"
    />
</template>

<script>
import SelectButton from 'primevue/selectbutton'
import InputText from 'primevue/inputtext'

export default {
    name: 'CoreSelectButton',

    components: { InputText, SelectButton },

    props: {
        modelValue: {
            type: [Number, String, Boolean],
            default: null,
        },
        name: {
            type: String,
            default: '',
        },
        options: {
            required: true,
            type: Array,
        },
        validator: {
            type: Object,
            default: null,
        },
        disabled: {
            type: Boolean,
            default: false,
        },

        fullWidth: {
            type: Boolean,
            default: false,
        },

        allowEmpty: {
            type: Boolean,
            default: true,
        },
    },

    emits: ['update:modelValue'],

    mounted() {
        if (this.validator) {
            if (this.modelValue === null || this.modelValue === '') {
                this.validator.$reset()
            } else {
                this.validator.$touch()
            }
        }
    },

    computed: {
        selected: {
            get() {
                return this.options.filter((option) => option.value === this.modelValue)[0]
            },

            set(newValue) {
                this.$emit('update:modelValue', newValue.value)

                if (this.validator) {
                    this.validator.$touch()
                }
            },
        },

        classes() {
            return {
                'core-select-button': true,
                'core-select-button--disabled': this.disabled,
                'core-valid': this.validator && !this.validator.$invalid && this.validator.$dirty,
                'core-invalid': this.validator && this.validator.$invalid && this.validator.$dirty,
                'core-warning': false,
                'core-select-button--full-width': this.fullWidth,
            }
        },

        validatorErrors() {
            if (this.validator.$error === undefined) {
                return {}
            }
            return this.validator.$error
        },

        validatorParams() {
            if (this.validator.$params === undefined) {
                return {}
            }
            return this.validator.$params
        },
    },
}
</script>

<style>
.core-select-button.p-selectbutton {
    display: flex;
    gap: 0;
}

.core-select-button.p-selectbutton.core-select-button--full-width > .p-button,
.core-select-button.p-selectbutton.core-select-button--full-width > .p-togglebutton {
    flex: 1 1 0;
    min-width: 0;
}

.core-select-button.p-selectbutton > .p-togglebutton,
.core-select-button.p-selectbutton > .p-togglebutton.p-togglebutton-checked {
    min-width: 10rem;
    border: 1px solid var(--color-primary-600);
    padding: 14px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    background: var(--color-white);
    color: var(--text-dark);
    font-size: 1.4rem;

    &:hover {
        background: var(--color-primary-400);
        color: var(--text-dark);
    }
}

.core-select-button.p-selectbutton.core-select-button--full-width > .p-togglebutton,
.core-select-button.p-selectbutton.core-select-button--full-width
    > .p-togglebutton.p-togglebutton-checked {
    flex: 1 1 0;
    min-width: 0;
}

.core-select-button.core-select-button--full-width {
    width: 100%;
}

.core-select-button.core-select-button--full-width.p-selectbutton > .p-togglebutton,
.core-select-button.core-select-button--full-width.p-selectbutton
    > .p-togglebutton.p-togglebutton-checked {
    flex: 1 1 0;
    min-width: 0;
    max-width: none;
}

.core-select-button.p-selectbutton > .p-togglebutton.p-togglebutton-checked {
    background: var(--color-primary-500);
}

.core-select-button.p-selectbutton > .p-togglebutton > .p-togglebutton-content {
    background: transparent;
}

.core-select-button.p-selectbutton > .p-togglebutton:first-child,
.core-select-button.p-selectbutton > .p-togglebutton.p-togglebutton-checked:first-child {
    border-radius: 100px 0 0 100px;
    border-right: none;
}

.core-select-button.p-selectbutton > .p-togglebutton:last-child,
.core-select-button.p-selectbutton > .p-togglebutton.p-togglebutton-checked:last-child {
    border-radius: 0 100px 100px 0;
    border-left: none;
}

.core-select-button.core-select-button--disabled.p-selectbutton > .p-togglebutton,
.core-select-button.core-select-button--disabled.p-selectbutton
    > .p-togglebutton.p-togglebutton-checked {
    border: 1px solid var(--color-neutral-500);
    cursor: pointer;
    background: var(--color-neutral-400);
    color: var(--color-secondary-200);

    &:hover {
        background: var(--color-neutral-400);
        color: var(--color-secondary-200);
    }
}

.core-select-button.p-selectbutton.core-select-button--disabled
    > .p-togglebutton.p-togglebutton-checked {
    background: var(--color-neutral-600);

    &:hover {
        background: var(--color-neutral-600);
    }
}
</style>
